import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React from 'react';
import {
  ProcessingType,
  PROCESSING_TYPES,
  PROCESSING_TYPE_DESCRIPTIONS,
  PR_PROCESSING_TYPES,
} from '../../../types';

export type ModEligibilityDialogAction = 'enable' | 'disable' | 'set-type';

export interface ModEligibilityDialogMod {
  modCoverageId: number;
  tagName: string;
  processingType: ProcessingType | null;
  isComplianceEnabled: boolean;
}

interface ModEligibilityConfirmDialogProps {
  isOpen: boolean;
  action: ModEligibilityDialogAction;
  mods: ModEligibilityDialogMod[];
  processingType: ProcessingType | '';
  onProcessingTypeChange: (value: ProcessingType | '') => void;
  onConfirm: () => void;
  onClose: () => void;
  isSubmitting: boolean;
}

const TITLES: Record<ModEligibilityDialogAction, string> = {
  enable: 'Enable PC & PR',
  disable: 'Disable PC & PR',
  'set-type': 'Set processing type',
};

const CONFIRM_CLASSES: Record<ModEligibilityDialogAction, string> = {
  enable: 'bg-green-600 hover:bg-green-700',
  disable: 'bg-red-600 hover:bg-red-700',
  'set-type': 'bg-blue-600 hover:bg-blue-700',
};

export const ModEligibilityConfirmDialog: React.FC<ModEligibilityConfirmDialogProps> = ({
  isOpen,
  action,
  mods,
  processingType,
  onProcessingTypeChange,
  onConfirm,
  onClose,
  isSubmitting,
}) => {
  if (!isOpen) return null;

  const title = TITLES[action];
  const confirmClass = CONFIRM_CLASSES[action];

  // PR-pipeline values are only selectable when every selected mod has PC & PR enabled.
  const allComplianceEnabled = mods.length > 0 && mods.every((m) => m.isComplianceEnabled);

  // Disabling PC & PR leaves PR-typed mods inconsistent — warn, but allow (backend keeps
  // processing_type as-is; the list shows a warning icon afterwards).
  const prTypedMods =
    action === 'disable'
      ? mods.filter(
          (m) => m.processingType !== null && PR_PROCESSING_TYPES.includes(m.processingType)
        )
      : [];

  const confirmDisabled = isSubmitting || (action === 'set-type' && processingType === '');

  const intro =
    action === 'set-type'
      ? `You are about to change the processing type for the following ${mods.length} mod${
          mods.length === 1 ? '' : 's'
        }:`
      : `You are about to ${action} PC & PR for the following ${mods.length} mod${
          mods.length === 1 ? '' : 's'
        }:`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="text-sm text-gray-700 mb-3">{intro}</p>
          {mods.length < 10 ? (
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
              {mods.map((mod) => (
                <div
                  key={mod.modCoverageId}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <span className="font-medium">{mod.tagName}</span>
                  <span className="text-gray-400 text-xs">
                    {mod.processingType ?? '—'} · #{mod.modCoverageId}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="max-h-72 overflow-y-auto border rounded-md px-3 py-2 text-sm"
              style={{ overflowWrap: 'normal', wordBreak: 'normal' }}
            >
              {mods.map((m) => m.tagName.replace(/-/g, '\u2011')).join(', ')}
            </div>
          )}

          {action === 'set-type' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New processing type
              </label>
              <select
                value={processingType}
                onChange={(e) => onProcessingTypeChange(e.target.value as ProcessingType | '')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select processing type…</option>
                {PROCESSING_TYPES.map((pt) => {
                  const isPr = PR_PROCESSING_TYPES.includes(pt);
                  const blocked = isPr && !allComplianceEnabled;
                  const description = PROCESSING_TYPE_DESCRIPTIONS[pt];
                  return (
                    <option key={pt} value={pt} disabled={blocked}>
                      {pt}
                      {description ? ` (${description})` : ''}
                      {blocked ? ' — requires PC & PR enabled on all selected mods' : ''}
                    </option>
                  );
                })}
              </select>
              {!allComplianceEnabled && (
                <p className="text-xs text-gray-500 mt-1">
                  PR processing types are disabled because some selected mods do not have PC &amp;
                  PR enabled.
                </p>
              )}
            </div>
          )}

          {prTypedMods.length > 0 && (
            <div className="mt-4 flex gap-2 border border-amber-300 bg-amber-50 rounded-md px-3 py-2">
              <WarningAmberIcon className="text-amber-500 shrink-0" fontSize="small" />
              <p className="text-sm text-amber-800">
                {prTypedMods.length === 1
                  ? `Mod ${prTypedMods[0].tagName} has`
                  : `${prTypedMods.length} selected mods have`}{' '}
                a PR processing type (
                {prTypedMods
                  .slice(0, 5)
                  .map((m) => m.tagName)
                  .join(', ')}
                {prTypedMods.length > 5 ? ', …' : ''}). Disabling PC &amp; PR will leave the
                processing type unchanged and inconsistent — these mods will be flagged with a
                warning on the list until the processing type is changed.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {isSubmitting ? 'Working...' : title}
          </button>
        </div>
      </div>
    </div>
  );
};
